'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import {
  getProfileByIdService,
  getProfileChangelogService,
  getProfileListService,
} from '@/services/httpServices/ProfileServices';
import type {
  ProfileByIdServiceConfig,
  ProfileChangelogServiceConfig,
  ProfileListResponse,
  ProfileListServiceConfig,
} from '@/services/httpServices/ProfileServices/types';

import { buildStandardPaginatedParams } from './createInfiniteQuery';
import { infiniteProfileListKeys, profileByIdKeys, profileChangelogKeys, profileListKeys } from './profileQueryKeys';
import { useQueryConfig } from './useQueryConfig';

export function useProfileListQuery(params: ProfileListServiceConfig) {
  const { isEnabled } = useQueryConfig();

  return useQuery({
    queryKey: profileListKeys(params),
    queryFn: async () => getProfileListService(params),
    enabled: isEnabled,
  });
}

/**
 * Hook to fetch profile list with infinite scrolling using cursor-based pagination
 * @param initialParams - Initial profile list query parameters
 * @param enabled - Whether to enable the query
 * @returns Infinite query result for the profile list
 */
export function useInfiniteProfileListQuery(
  initialParams: ProfileListServiceConfig,
  enabled: boolean = true,
) {
  const { isEnabled } = useQueryConfig();

  return useInfiniteQuery<ProfileListResponse>({
    queryKey: infiniteProfileListKeys(initialParams),
    queryFn: async ({ pageParam }) => {
      const paginationParams = buildStandardPaginatedParams(
        initialParams,
        pageParam ? String(pageParam) : undefined,
      );
      return getProfileListService(paginationParams);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage === true && lastPage.pageInfo.endCursor !== null) {
        return lastPage.pageInfo.endCursor;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.pageInfo.hasPreviousPage === true && firstPage.pageInfo.startCursor !== null) {
        return firstPage.pageInfo.startCursor;
      }
      return undefined;
    },
    enabled: isEnabled && enabled,
  });
}

export function useProfileByIdQuery(config: ProfileByIdServiceConfig) {
  const { isEnabled } = useQueryConfig();

  return useQuery({
    queryKey: profileByIdKeys(config),
    queryFn: async () => getProfileByIdService(config),
    enabled: isEnabled && config.params?.id !== undefined && config.params?.id !== null && config.params.id !== '',
  });
}

export function useProfileChangelogQuery(config: ProfileChangelogServiceConfig) {
  const { isEnabled } = useQueryConfig();

  return useQuery({
    queryKey: profileChangelogKeys(config),
    queryFn: async () => getProfileChangelogService(config),
    enabled: isEnabled,
  });
}
